import reportorderstatusService from '../../../services/reportorderstatusService';
import filterService from '../../../services/filterService';

import {
    setReportOrderStatusLoading,
    setReportOrderStatusError,
    setReportOrderStatusList,
    setReportOrderStatusCurrent,
    setReportOrderStatusPagination,
    setReportOrderStatusSort,
    setReportOrderStatusFilters,
    setReportOrderStatusDefaultFilter,
    setReportOrderStatusSettings,
    clearReportOrderStatusCurrent,
    clearReportOrderStatusError,
    setReportOrderStatusFilterLoading,
    changeReportOrderStatusFilterCondition,
    addReportOrderStatusFilterCondition,
    updateReportOrderStatusFilterConditionRule,
    updateReportOrderStatusFilterConditionValue,
    removeReportOrderStatusFilterCondition,
    setReportOrderStatusSelected,
    setReportOrderStatusDownloading,
    resetReportOrderStatusFiltersAndPagination,

    setReportOrderStatusClassicFilters
} from './reportorderstatusSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportorderstatus.isSettingsInitialized) {
            dispatch(setReportOrderStatusLoading(true));
            const response = await reportorderstatusService.getSettings();

            // Set the settings
            dispatch(setReportOrderStatusSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportOrderStatusClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportOrderStatusDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportOrderStatusError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportOrderStatusLoading(false));
    }
};

export const fetchReportorderstatuses = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportorderstatus;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportOrderStatusLoading(true));
        const response = await reportorderstatusService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportOrderStatusList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportorderstatuses:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportOrderStatusError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportOrderStatusLoading(false));
    }
};

export const fetchReportOrderStatus = (id) => async (dispatch) => {
    try {
        dispatch(setReportOrderStatusLoading(true));
        const response = await reportorderstatusService.getOne(id);
        dispatch(setReportOrderStatusCurrent(response.data));
    } catch (error) {
        dispatch(setReportOrderStatusError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportOrderStatusLoading(false));
    }
};

export const createReportOrderStatus = (data) => async (dispatch) => {
    try {
        const response = await reportorderstatusService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportOrderStatus = (id, data) => async (dispatch) => {
    try {
        const response = await reportorderstatusService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportOrderStatus = (id) => async (dispatch) => {
    try {
        dispatch(setReportOrderStatusLoading(true));
        const response = await reportorderstatusService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportOrderStatusError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportOrderStatusLoading(false));
    }
};

export const downloadReportOrderStatus = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportorderstatus;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportOrderStatusDownloading(true));
        const response = await reportorderstatusService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportOrderStatusDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportorderstatuses:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportOrderStatusError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportOrderStatusDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportOrderStatusPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportOrderStatusSort(sort));
};

export const searchReportorderstatuses = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportorderstatus.filters;
    dispatch(setReportOrderStatusFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportOrderStatusDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportOrderStatusFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportOrderStatusFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportOrderStatusFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportOrderStatusFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportOrderStatusFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportOrderStatusFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportorderstatus.settings;
            dispatch(setReportOrderStatusSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportOrderStatusFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportOrderStatusFilterLoading(false));
        dispatch(setReportOrderStatusError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportOrderStatusSelected(selectedRows));
    };
};

export const clearCurrentReportOrderStatus = () => {
    return (dispatch) => {
        dispatch(clearReportOrderStatusCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportOrderStatusFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportorderstatus.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportOrderStatusClassicFilters(updatedClassicFilters));
};